// Reads a zip file a piece at a time. Only the directory at the end of the file is read up front.
// Each file inside is read from its own slice when it is needed, so a large export with photos and
// videos never has to fit in memory at once. fflate inflates the compressed files.

import { Inflate } from "fflate";

export type ZipEntry = {
  name: string;
  size: number;
  compressedSize: number;
  method: number;
  /** Where the file's local header starts. */
  offset: number;
};

export class ZipError extends Error {}

const CHUNK = 1024 * 1024;
const EOCD = 0x06054b50;
const ZIP64_LOCATOR = 0x07064b50;
const ZIP64_EOCD = 0x06064b50;
const CENTRAL = 0x02014b50;
const LOCAL = 0x04034b50;
const MAX32 = 0xffffffff;

async function bytesOf(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function u64(view: DataView, at: number): number {
  return view.getUint32(at, true) + view.getUint32(at + 4, true) * 2 ** 32;
}

/** Lists the files in a zip. Folders, macOS resource forks and encrypted files are left out. */
export async function readZipDirectory(blob: Blob): Promise<ZipEntry[]> {
  const tailStart = Math.max(0, blob.size - (65_535 + 22));
  const tail = await bytesOf(blob, tailStart, blob.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new ZipError("This is not a zip file, or it is damaged.");
  let count = tail.getUint16(eocd + 10, true);
  let size = tail.getUint32(eocd + 12, true);
  let start = tail.getUint32(eocd + 16, true);
  if ((count === 0xffff || size === MAX32 || start === MAX32) && eocd >= 20) {
    if (tail.getUint32(eocd - 20, true) === ZIP64_LOCATOR) {
      const at = u64(tail, eocd - 20 + 8);
      const z = await bytesOf(blob, at, at + 56);
      if (z.getUint32(0, true) !== ZIP64_EOCD) throw new ZipError("This zip file is damaged.");
      count = u64(z, 32);
      size = u64(z, 40);
      start = u64(z, 48);
    }
  }
  const dir = await bytesOf(blob, start, start + size);
  const decoder = new TextDecoder("utf-8");
  const entries: ZipEntry[] = [];
  let p = 0;
  for (let n = 0; n < count && p + 46 <= dir.byteLength; n++) {
    if (dir.getUint32(p, true) !== CENTRAL) throw new ZipError("This zip file is damaged.");
    const flags = dir.getUint16(p + 8, true);
    const method = dir.getUint16(p + 10, true);
    let compressedSize = dir.getUint32(p + 20, true);
    let uncompressed = dir.getUint32(p + 24, true);
    const nameLength = dir.getUint16(p + 28, true);
    const extraLength = dir.getUint16(p + 30, true);
    const commentLength = dir.getUint16(p + 32, true);
    let offset = dir.getUint32(p + 42, true);
    const name = decoder.decode(new Uint8Array(dir.buffer, dir.byteOffset + p + 46, nameLength));
    // ZIP64 keeps the large values in an extra field, in this order, only for those that overflow.
    let e = p + 46 + nameLength;
    const extraEnd = e + extraLength;
    while (e + 4 <= extraEnd) {
      const id = dir.getUint16(e, true);
      const length = dir.getUint16(e + 2, true);
      if (id === 0x0001) {
        let q = e + 4;
        if (uncompressed === MAX32) {
          uncompressed = u64(dir, q);
          q += 8;
        }
        if (compressedSize === MAX32) {
          compressedSize = u64(dir, q);
          q += 8;
        }
        if (offset === MAX32) offset = u64(dir, q);
      }
      e += 4 + length;
    }
    p = extraEnd + commentLength;
    const encrypted = (flags & 1) === 1;
    if (name.endsWith("/") || name.startsWith("__MACOSX/") || encrypted) continue;
    entries.push({ name, size: uncompressed, compressedSize, method, offset });
  }
  return entries;
}

/** Reads one file out of the zip. */
export async function readZipEntry(blob: Blob, entry: ZipEntry, type = ""): Promise<Blob> {
  const local = await bytesOf(blob, entry.offset, entry.offset + 30);
  if (local.getUint32(0, true) !== LOCAL) throw new ZipError("This zip file is damaged.");
  const dataStart = entry.offset + 30 + local.getUint16(26, true) + local.getUint16(28, true);
  const data = blob.slice(dataStart, dataStart + entry.compressedSize);
  if (entry.method === 0) return new Blob([data], { type });
  if (entry.method !== 8) throw new ZipError(`${entry.name} uses a kind of compression Rhema cannot read.`);
  const out: Uint8Array<ArrayBuffer>[] = [];
  const inflater = new Inflate((chunk) => {
    out.push(chunk as Uint8Array<ArrayBuffer>);
  });
  for (let at = 0; at < data.size; at += CHUNK) {
    const piece = new Uint8Array(await data.slice(at, at + CHUNK).arrayBuffer());
    inflater.push(piece, at + CHUNK >= data.size);
  }
  if (data.size === 0) inflater.push(new Uint8Array(0), true);
  return new Blob(out, { type });
}

/** Whether the first bytes are a zip file's signature. */
export async function isZip(blob: Blob): Promise<boolean> {
  if (blob.size < 4) return false;
  const head = await bytesOf(blob, 0, 4);
  return head.getUint32(0, true) === LOCAL;
}
